// Страница «Мои поездки»: /trips.
//
// Отдельный экран, а не выпадающий список в тулбаре: поездок бывает много, у
// каждой есть название, даты, состав соавторов и своя публичная ссылка — в меню
// на одну строку это не помещается.
//
// Список разбит надвое: свои поездки и те, куда позвали соавтором. Это разные
// права (переименовать и удалить можно только своё), и мешать их в одну кучу
// значит каждый раз перечитывать, чья строка перед тобой.
import { useEffect, useState, type ReactElement } from 'react';
import { useT, useI18n } from '../i18n';
import { Logo, MobileShellHeader, useIsMobile } from '../ui/shell';
import { ConfirmDialog } from '../ui/forms';
import { AccountButton, useAuth } from '../auth';
import { shareUrl, useShareView } from '../share';
import { TripTemplates, templateState, type TripTemplate } from '../templates';
import { navigate } from '../router';
import { useTrips } from './TripsProvider';
import { TripAccessModal } from './TripAccessModal';
import { TripInfoPopover } from './TripInfoPopover';
import { formatTripDateCompact } from './tripFormat';
import type { TripMeta } from './api';
import styles from './TripsPage.module.css';

/** Пиктограммы кнопок ряда действий — по одной на каждое действие строки. */
function OpenIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
    </svg>
  );
}

function RenameIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function AccessIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="8" cy="9" r="3" />
      <path d="M2.5 20a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M14.5 12.2a5 5 0 0 1 7 6.8" />
    </svg>
  );
}

function LinkIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10 13a4 4 0 0 0 5.7.3l2-2a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M14 11a4 4 0 0 0-5.7-.3l-2 2a4 4 0 0 0 5.7 5.7l1-1" />
    </svg>
  );
}

function TrashIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** Строка списка: открыть, переименовать, доступ, скопировать ссылку, удалить. */
function TripRow({
  trip,
  open,
  onOpen,
  onRename,
  onAccess,
  onDelete,
}: {
  trip: TripMeta;
  open: boolean;
  onOpen(): void;
  onRename(name: string): void;
  onAccess(): void;
  onDelete(): void;
}): ReactElement {
  const { t, lang } = useI18n();
  // Ссылка отсюда должна открываться так же, как скопированная из окна
  // «поделиться»: одна и та же поездка, один и тот же вид.
  const view = useShareView();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(trip.name);
  const [copied, setCopied] = useState(false);

  const title = trip.name.trim() || t('trips.untitled');
  const isOwner = trip.role === 'owner';

  function commit(): void {
    setEditing(false);
    const next = draft.trim();
    if (next !== trip.name) onRename(next);
  }

  async function copyLink(): Promise<void> {
    if (!trip.shareId) return;
    try {
      await navigator.clipboard.writeText(shareUrl(trip.shareId, view));
      setCopied(true);
    } catch {
      // Буфер недоступен (нет https или отказано в праве) — ссылка всё равно
      // видна на странице поездки, копировать можно оттуда.
      setCopied(false);
    }
  }

  return (
    <li className={`${styles.row} ${open ? styles.rowOpen : ''}`}>
      <div className={styles.rowMain}>
        {editing ? (
          <input
            className={`input ${styles.nameInput}`}
            value={draft}
            maxLength={80}
            autoFocus
            aria-label={t('trips.name')}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setDraft(trip.name);
                setEditing(false);
              }
            }}
          />
        ) : (
          <button type="button" className={styles.name} onClick={onOpen}>
            {title}
          </button>
        )}

        {/* Раньше здесь были обе даты текстом и бейджи роли/соавторов/ссылки —
            самая тяжёлая по весу часть плитки на телефоне. Осталась только
            дата последней правки в коротком виде (по ней список и
            отсортирован) плюс кнопка «ⓘ»: дата создания, роль, состав
            соавторов и статус ссылки никуда не делись, просто переехали в
            поповер — по запросу, а не всегда на виду. «Открыта сейчас» — не
            метаданные, а текущее состояние, поэтому бейдж остался тут же. */}
        {/* div, а не p: внутри — попап (TripInfoPopover рисует свой div), а
            div внутри p — невалидный HTML, браузер тихо закрывает абзац
            раньше времени и рвёт этот же flex-ряд пополам. */}
        <div className={styles.meta}>
          {open && <span className={styles.openBadge}>{t('trips.open')}</span>}
          <span className={styles.updated}>{formatTripDateCompact(trip.updatedAt, lang)}</span>
          <TripInfoPopover trip={trip} />
        </div>
      </div>

      <div className={styles.rowActions}>
        <button type="button" className={`btn btn-sm ${styles.actionBtn}`} onClick={onOpen} disabled={open}>
          <OpenIcon />
          {t('trips.openAction')}
        </button>
        {isOwner && (
          <button
            type="button"
            className={`btn btn-sm ${styles.actionBtn}`}
            onClick={() => {
              setDraft(trip.name);
              setEditing(true);
            }}
          >
            <RenameIcon />
            {t('trips.rename')}
          </button>
        )}
        <button type="button" className={`btn btn-sm ${styles.actionBtn}`} onClick={onAccess}>
          <AccessIcon />
          {t('trips.access')}
        </button>
        {trip.shareId && (
          <button type="button" className={`btn btn-sm ${styles.actionBtn}`} onClick={() => void copyLink()}>
            <LinkIcon />
            {t(copied ? 'share.copied' : 'trips.copyLink')}
          </button>
        )}
        <button type="button" className={`btn btn-sm btn-danger ${styles.actionBtn}`} onClick={onDelete}>
          <TrashIcon />
          {t(isOwner ? 'trips.delete' : 'trips.leave')}
        </button>
      </div>
    </li>
  );
}

export function TripsPage(): ReactElement {
  const t = useT();
  const isMobile = useIsMobile();
  const { status: authStatus, user, requestSignIn } = useAuth();
  const trips = useTrips();
  const [creating, setCreating] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [accessId, setAccessId] = useState<string | null>(null);

  // Список мог устареть, пока человек работал в поездке: даты правки берутся
  // с сервера, а не из того, что лежало в памяти со входа.
  useEffect(() => {
    if (authStatus === 'user') void trips.refresh();
    // refresh стабилен (useCallback в провайдере), перечитываем на вход
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  async function handleCreate(): Promise<void> {
    setCreating(true);
    const trip = await trips.create(t('trips.newName'));
    setCreating(false);
    if (trip) navigate('/');
  }

  /**
   * Поездка из шаблона называется по шаблону: имя «Новая поездка» рядом с
   * готовым содержимым выглядело бы как чужая заготовка, а не своя поездка.
   */
  async function handleTemplate(tpl: TripTemplate): Promise<void> {
    setCreating(true);
    const trip = await trips.create(t(tpl.nameKey), templateState(tpl, t));
    setCreating(false);
    if (trip) navigate('/');
  }

  function handleOpen(id: string): void {
    trips.select(id);
    navigate('/');
  }

  const pending = trips.trips.find((x) => x.id === confirmId) ?? null;
  const access = trips.trips.find((x) => x.id === accessId) ?? null;

  function renderList(list: TripMeta[]): ReactElement {
    return (
      <ul className={styles.list}>
        {list.map((trip) => (
          <TripRow
            key={trip.id}
            trip={trip}
            open={trip.id === trips.currentId}
            onOpen={() => handleOpen(trip.id)}
            onRename={(name) => void trips.rename(trip.id, name)}
            onAccess={() => setAccessId(trip.id)}
            onDelete={() => setConfirmId(trip.id)}
          />
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.page}>
      {/* Мобильная шапка — общий компонент с планировщиком (лого-полоса на
          всю ширину, заголовок и единая кнопка меню с сайдбаром вместо
          бургера и попапа аккаунта). Живёт вне .content: та несёт боковые и
          верхние поля, а шапке нужно быть впритык к краям экрана. На
          десктопе компонент — no-op (сам возвращает null внутри), поэтому
          вставлять его безусловно можно и там. */}
      <MobileShellHeader title={t('trips.title')} />

      <div className={styles.content}>
        {/* Десктопная версия той же шапки. Ветвление через isMobile, а не
            через CSS-скрытие: если обе шапки одновременно жить в DOM, на
            странице окажется два <h1> разом, и скринридер зачитает заголовок
            дважды. Знак ведёт гостя на корень: поездок у него тут нет и быть
            не может. Вошедшему — без ссылки, он и так на месте. */}
        {!isMobile && (
          <header className={styles.header}>
            <Logo title={t('app.brand')} href={authStatus === 'guest' ? '/' : undefined} />
            <h1 className={styles.title}>{t('trips.title')}</h1>
            <AccountButton />
          </header>
        )}

        {authStatus === 'loading' && <p className={styles.muted}>{t('auth.busy')}</p>}

        {/* Гостю тут нечего смотреть: список поездок — свойство аккаунта, а у
            гостя всё живёт в браузере одним состоянием. Экран — пустое
            состояние: одна центрированная карточка с приглашением войти, а не
            абзац, прижатый к шапке. */}
        {authStatus === 'guest' && (
          <div className={styles.guestWrap}>
            <div className={styles.guestCard}>
              <h2 className={styles.guestTitle}>{t('trips.guest.title')}</h2>
              <p className={styles.muted}>{t('trips.guest')}</p>
              <button
                type="button"
                className={`btn btn-primary ${styles.guestSignIn}`}
                onClick={requestSignIn}
              >
                {t('auth.signin')}
              </button>
              {/* Выход отсюда для гостя: без него единственной дорогой назад
                  остаётся знак в шапке, а знак — не кнопка «назад». */}
              <a className={styles.back} href="/">
                {t('trips.back')}
              </a>
            </div>
          </div>
        )}

        {authStatus === 'user' && (
          <>
            {/* Ссылка «к планировщику» отсюда убрана (C8): у вошедшего и так
                есть дорога назад — знак в шапке. У гостя её нет (знак ведёт на
                корень для него тоже), поэтому в гостевой карточке ссылка
                остаётся. */}
            <div className={styles.toolbar}>
              <button
                type="button"
                className={`btn btn-primary ${styles.create}`}
                disabled={creating}
                onClick={() => void handleCreate()}
              >
                {t('trips.create')}
              </button>
            </div>

            {/* Шаблоны стоят под кнопкой создания и по умолчанию свёрнуты: это
                боковой путь для того, кто не знает, с чего начать, а не то, через
                что должен проходить каждый. Тому, у кого поездок ещё нет,
                раскрываем сразу — там пустой экран и подсказать уместно. */}
            {trips.status === 'ready' && (
              <TripTemplates
                actionKey="tpl.create"
                noteKey="tpl.page.note"
                defaultOpen={trips.trips.length === 0}
                busy={creating}
                onPick={(tpl) => void handleTemplate(tpl)}
              />
            )}

            {trips.status === 'loading' && <p className={styles.muted}>{t('auth.busy')}</p>}

            {trips.status === 'ready' && trips.trips.length === 0 && (
              <p className={styles.muted}>{t('trips.empty')}</p>
            )}

            {trips.owned.length > 0 && (
              <section className={styles.group}>
                <h2 className={styles.groupTitle}>{t('trips.section.mine')}</h2>
                {renderList(trips.owned)}
              </section>
            )}

            {/* Чужие поездки — отдельным разделом и только когда они есть: пустой
                заголовок «Общие» ничего не объясняет тому, с кем не делились. */}
            {trips.shared.length > 0 && (
              <section className={styles.group}>
                <h2 className={styles.groupTitle}>{t('trips.section.shared')}</h2>
                <p className={styles.muted}>{t('trips.section.shared.hint')}</p>
                {renderList(trips.shared)}
              </section>
            )}
          </>
        )}

        {access && (
          <TripAccessModal
            trip={access}
            onClose={() => setAccessId(null)}
            onChanged={() => void trips.refresh()}
          />
        )}

        <ConfirmDialog
          open={pending !== null}
          title={t(pending?.role === 'owner' ? 'trips.delete.title' : 'trips.leave.title')}
          message={t(pending?.role === 'owner' ? 'trips.delete.text' : 'trips.leave.text', {
            name: pending?.name.trim() || t('trips.untitled'),
          })}
          confirmLabel={t(pending?.role === 'owner' ? 'trips.delete' : 'trips.leave')}
          danger
          onConfirm={() => {
            // Своя поездка удаляется, чужая — просто теряет тебя в соавторах.
            if (pending?.role === 'owner') void trips.remove(pending.id);
            else if (pending && user) void trips.leave(pending.id, user.id);
            setConfirmId(null);
          }}
          onCancel={() => setConfirmId(null)}
        />
      </div>
    </div>
  );
}
